<template>
  <div>
    <ConfirmationDialog2 ref="confirmationDialog" />
    <v-dialog v-model="dialog" :fullscreen="$vuetify.breakpoint.smAndDown" persistent content-class="service-dialog" max-width="600px">
      <template v-if="dialog">
        <v-flex>
          <v-card flat dark>
            <v-toolbar dark color="primary">
              <v-toolbar-title>Save Query {{ isSomeAdmin }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon @click.native="close()" dark>
                <v-icon>close</v-icon>
              </v-btn>
            </v-toolbar>
          </v-card>
        </v-flex>
        <v-flex style="overflow: auto;">
          <v-card flat>
            <v-card-text style="py-2 min-height: 55px;" class="pt-1 pb-0 text-xs-center caption">
              <v-alert v-if="loading.name" color="success" class="py-2 no-border" outline :value="true">
                <v-progress-circular size="24" color="primary" indeterminate />
              </v-alert>
              <template v-else>
                <v-alert icon="check_circle" class="py-2 no-border" outline color="success" :value="saveState == saveStates.createNew">
                  A new query will be created for <span class="font-weight-medium">{{ selectedUsername }}</span>
                </v-alert>

                <v-alert icon="info" class="py-2 no-border" outline color="info" :value="saveState == saveStates.updateExisting">
                  <span v-if="isSomeAdmin && !isCurrentUser(selectedUser)">
                    The selected query will be updated for <strong>{{ selectedUsername }}</strong>
                  </span>
                  <span v-else>
                    <span class="font-weight-medium">Your</span> selected query will be updated
                  </span>
                </v-alert>

                <v-alert icon="warning" class="py-2 no-border" outline color="warning" :value="saveState == saveStates.overwriteExisting">
                  <span v-if="isSomeAdmin && !isCurrentUser(selectedUser)">
                    One of <strong>{{ selectedUsername }}</strong>'s
                    queries (with the same name) will be overwritten
                  </span>
                  <span v-else>
                    One of your other queries (with the same name) will be overwritten
                  </span>
                </v-alert>

              </template>
            </v-card-text>
            <v-divider />
            <v-card-text class="pt-0">
              <v-container class="pt-0">
                <Form :disabled="loading.saving" ref="form" @update:formData="formData = $event" @update:isValid="isValid = $event" @update:selectedUser="selectedUser = $event" />
              </v-container>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex>
          <v-card style="border-top: 1px solid #dddddd;">
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn :color="saveState.color" :loading="loading.saving" @click="submit()">
                <v-icon v-if="saveState.icon" class="mr-1">{{ saveState.icon }}</v-icon>
                {{ saveState.text }}
              </v-btn>
              <v-btn depressed :disabled="loading.saving" @click="close()">{{$t('Button.Cancel')}}</v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
      </template>
    </v-dialog>
  </div>
</template>

<script>
  import Form from './Form'
  import {
    saveStates,
  } from '@/helpers/multiStationQuery'

  import {
    debounce,
  } from '@/helpers/performance'
  const debounceTimer = 600

  export default {
    components: {
      Form,
    },
    props: {
      service: Object,
    },
    data: () => ({
      saveStates,
      nameExists: false,
      selectedUser: null,
      dialog: false,
      formData: null,
      initial: {
        id: null,
        name: null,
        userId: null,
      },

      queryDetails: null,
      queryData: null,
      optionsData: null,
      isValid: false,
      loading: {
        name: false,
        saving: false,
      },
    }),
    computed: {
      reportGenerator() {
        return this.$store.getters['multistation/reportGenerator']
      },
      currentUser() {
        return this.$store.getters['authenticate/currentUser']
      },
      selectedUsername() {
        if (this.isCurrentUser(this.selectedUser)) return 'you'
        else return this.selectedUser && (this.selectedUser.username || this.selectedUser.UserName)
      },
      isSomeAdmin() {
        return this.$store.getters['authenticate/isSomeAdmin']
      },
      saveState() {
        if (this.nameExists) {
          if (this.initial.name == this.formData.name &&
            this.initial.userId == this.formData.userId) {
            return saveStates.updateExisting
          }

          return saveStates.overwriteExisting
        }

        return saveStates.createNew
      },
      debouncedExists() {
        return debounce(function exec() {
          this.fetchExists()
        }, debounceTimer)
      },
    },
    watch: {
      isSomeAdmin: {
        immediate: true,
        handler(value) {
          if (value) {
            this.fetchUserList()
          }
        },
      },
      'formData.name'() {
        this.$nextTick().then(() => {
          this.loading.name = true
          this.debouncedExists()
        })
      },
      selectedUser: {
        deep: true,
        handler() {
          this.$nextTick(() => {
            this.loading.name = true
            this.debouncedExists()
          })
        },
      },
    },
    methods: {
      async fetchExists() {
        let service = this.reportGenerator
        const name = this.formData.name

        if (!service || !name) return

        this.loading.name = true

        const userId = (this.selectedUser &&
          (
            this.selectedUser.userId ||
            this.selectedUser.UserId
          )
        )

        let payload = {
          name,
          userId,
        }

        try {
          let exists = await this.$store.dispatch('multistation/checkExisting', {
            service,
            payload,
          })

          this.nameExists = exists
          this.loading.name = false
        } catch (e) {
          console.error(e)
        }
      },
      makePayload() {
        const optionsData = this.optionsData
        let queryParams = JSON.stringify(optionsData)

        return {
          ...this.formData,
          queryParams,
        }
      },
      isCurrentUser(user) {
        if (!user) return true
        let userId = user.userId || user.UserId
        return this.currentUser.userId == userId
      },
      show(queryDetails, queryData, optionsData) {
        this.initial = {
          name: queryDetails.name,
          userId: queryDetails.userId,
        }

        this.queryDetails = queryDetails
        this.queryData = queryData
        this.optionsData = optionsData

        this.dialog = true

        if (!this.isSomeAdmin && queryDetails.userId != this.currentUser.userId) {
          queryDetails.userId = null
          queryDetails.id = null
        }

        this.$nextTick(() => {
          this.$refs.form.setQueryDetails(queryDetails)
        })
      },
      close() {
        this.reset()
        this.dialog = false
      },
      async submit() {
        if (this.saveState.confirmParams) {
          let confirm = await this.$refs.confirmationDialog.confirm({
            ...this.saveState.confirmParams,
          })
          if (!confirm) return
        }

        this.$refs.form.validate()
        if (!this.isValid) return

        let action
        if (this.saveState.isUpdate) {
          action = 'multistation/updateQuery'
        } else {
          action = 'multistation/addQuery'
        }

        this.loading.saving = true
        let service = this.reportGenerator
        let query = this.makePayload()

        try {
          await this.$store.dispatch(action, {
            service,
            query,
          })

          this.loading.saving = false
          this.$store.dispatch('app/setSnackbar', {
            text: 'Query saved successfully',
            color: 'success',
            icon: 'done',
          })

          this.$emit('update:query', query)
          this.close()

        } catch (e) {
          this.loading.saving = false
          let response = e.response

          if (response.status < 500) {
            if (response.data.fieldValidations) {
              this.$refs.form.setErrorMessages(response.data.fieldValidations)
            } else {
              this.$store.dispatch('app/setSnackbar', {
                text: `${response.data.displayMessage}. ${response.data.reasonPhrase}`,
                color: 'error',
                icon: 'error',
              })
            }
          } else {
            this.$store.dispatch('app/setSnackbar', {
              text: 'Something unexpected went wrong, please let us know so we can fix it!',
              color: 'error',
              icon: 'error',
            })
          }
        }
      },
      fetchUserList() {
        this.$store.dispatch('admin/getUserList', {
          tenantOnly: true,
        })
      },
      reset() {
        this.queryData = null
      },
    },
  }
</script>

<style scoped>
  .v-dialog--fullscreen>.v-card {
    min-height: 85px !important;
  }

  .v-dialog--fullscreen>.v-card+.v-card {
    min-height: Calc(100% - 85px) !important;
  }

  .v-dialog--fullscreen>.v-card+.v-card .v-card__text {
    padding: 0px !important;
  }
</style>
<style>
  .v-alert.no-border {
    border: none !important;
  }

  .service-dialog {
    display: flex;
    flex-direction: column;
  }
</style>